/**
 * T-007 — Field/bookmark protection: the pure region map + predicates.
 *
 * Hard rule from the deep-dive: the engine must **never touch fields or bookmarks**. Cross-reference
 * fields (e.g. `Schedule [3.17]`) and bookmarks are atomic — no op may delete inside one, insert
 * inside one, or split one. The T-001 spike showed exactly why this matters: a field's result is
 * rendered as plain text fused to its neighbours in `range.text` (`"Disclosure Scheduleattached"`),
 * so an edit computed purely from text will happily land in the middle of a field unless something
 * stops it. This module is that something.
 *
 * The Word-facing extraction of the spans lives in `extractProtectedRegions.ts` (manual-verified in
 * Word, since it depends on Office.js). This file is the **pure, unit-tested** consumer contract:
 * given the spans, answer "is this offset / this edit range protected?" so the position resolver
 * (T-008) and pre-flight (T-009) can reject any op that would violate a protected span.
 *
 * ## Boundary rule (edit up to a field, never across it)
 * Regions are half-open `[start, end)`. Editing that abuts a field is allowed; editing that enters
 * it is not:
 *  - A deletion/replacement range `[a, b)` is protected iff it overlaps a region interior
 *    (`max(a, r.start) < min(b, r.end)`). A range that merely ends at `r.start` or begins at `r.end`
 *    does not overlap — you may edit right up to the boundary.
 *  - An insertion point `a` (empty range) is protected iff it is strictly inside a region
 *    (`r.start < a < r.end`). Inserting exactly at a boundary (`a === r.start` or `a === r.end`) is
 *    allowed — that is "adjacent to", not "inside", the field.
 */

import { ProtectedRegion } from './types';

export interface ProtectedRegionSet {
  /** The regions, validated and sorted by start. */
  readonly regions: readonly ProtectedRegion[];
  /** Is the character at `offset` inside a protected region? (`start <= offset < end`) */
  isProtected(offset: number): boolean;
  /**
   * Would an edit spanning `[start, end)` touch a protected region? For an insertion point pass
   * `start === end`. See the boundary rule above.
   */
  overlapsProtected(start: number, end: number): boolean;
  /** Text-free counts for observability. */
  count(): { field: number; bookmark: number; comment: number; total: number };
}

/**
 * Build the queryable protected-region set from raw spans (e.g. from `extractProtectedRegions`).
 * Validates each span and sorts by start; overlapping spans are allowed (a field and a bookmark may
 * coincide) and simply both consulted.
 */
/**
 * True when this XML fragment (ideally a single `<w:p>`) contains field,
 * bookmark, or comment markup that `extractProtectedRegions` must map.
 * Scan the isolated paragraph — not the fat `getOoxml()` package — so
 * leftover `comments.xml` parts do not force a no-op Office.js walk.
 */
export function paragraphXmlHasProtectedMarkup(xml: string): boolean {
  if (!xml) return false;
  if (xml.includes('\u0005')) return true;
  return /<(?:\w+:)?(?:fldChar|fldSimple|instrText|bookmarkStart|commentRangeStart|commentReference)\b/i.test(
    xml,
  );
}

export function createProtectedRegionSet(regions: ProtectedRegion[]): ProtectedRegionSet {
  for (const r of regions) {
    if (r.start < 0 || r.end < r.start) {
      throw new Error(
        `createProtectedRegionSet: invalid region [${r.start}, ${r.end}) of kind ${r.kind}`,
      );
    }
  }
  const sorted = [...regions].sort((a, b) => a.start - b.start || a.end - b.end);

  return {
    regions: sorted,

    isProtected(offset: number): boolean {
      return sorted.some((r) => r.start <= offset && offset < r.end);
    },

    overlapsProtected(start: number, end: number): boolean {
      if (start > end) {
        throw new Error(`overlapsProtected: start ${start} > end ${end}`);
      }
      if (start === end) {
        // insertion point: protected only when strictly inside a region
        return sorted.some((r) => r.start < start && start < r.end);
      }
      // range: protected when it intersects a region interior
      return sorted.some((r) => Math.max(start, r.start) < Math.min(end, r.end));
    },

    count() {
      let field = 0;
      let bookmark = 0;
      let comment = 0;
      for (const r of sorted) {
        if (r.kind === 'field') field++;
        else if (r.kind === 'comment') comment++;
        else bookmark++;
      }
      return { field, bookmark, comment, total: sorted.length };
    },
  };
}
